<template>
  <PageContainer>
    <View direction="column" gap="sm">
      <PageBackNav :teleport-slot-id="headerTeleportSlot" @back-nav="goBack(propertyRoute(propertyId))" />

      <View direction="row" class="wrapper" justify="space-between" gap="sm" v-if="renter && property">
        <View direction="column" gap="sm" :grow="1">
          <RenterProfileHeader :renter="renter" />
          <RenterProfileApplications />
          <RenterProfileInfoItemsCard title="Renter highlights" :info-items="renterHighlights">
            <template v-slot:badges>
              <RenterProfileHighlightsBadges :renter="renter" :property="property" />
            </template>
            <template v-slot:highlights>
              <RenterProfileDialogHighlights
                v-if="renterScreening.data?.value?.convoHighlights.length"
                :highlights="renterScreening.data?.value?.convoHighlights"
              />
            </template>
          </RenterProfileInfoItemsCard>
          <RenterProfileInfoItemsCard title="Contact Info" :info-items="contactInfoItems" />
        </View>

        <RenterProfilePropertyInquiries class="aside" />
      </View>
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { formatDate } from '@vueuse/core';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { formatCurrency, PageContainer, View, PageBackNav } from '@tallo/design-system';
import { propertyRoute, useNavigation } from '@tallo/investor/navigation';
import { usePropertyStore } from '@tallo/investor/property';

import { headerTeleportSlot } from '../root/header-teleport-slot.const';
import RenterProfileApplications from './components/renter-profile-applications.vue';
import RenterProfileDialogHighlights from './components/renter-profile-dialog-highlights.vue';
import RenterProfileHeader from './components/renter-profile-header.vue';
import RenterProfileHighlightsBadges from './components/renter-profile-highlights-badges.vue';
import RenterProfileInfoItemsCard from './components/renter-profile-info-items-card.vue';
import RenterProfilePropertyInquiries from './components/renter-profile-property-inquiries.vue';
import { useRenterScreeningQuery } from './queries/renter-screening.query';
import { useRenterProfileStore } from './store/renter-profile.store';

const route = useRoute();
const renterId = ref(route.params.renterId as string);
const propertyId = ref(route.params.propertyId as string);
const renterProfileStore = useRenterProfileStore();
const propertyStore = usePropertyStore();
const renterScreening = useRenterScreeningQuery();
const { goBack } = useNavigation();

const renter = computed(() => renterProfileStore.getRenterProfile);
const property = computed(() => propertyStore.property);

const contactInfoItems = computed<{ label: string; value: string | number | undefined }[]>(() => [
  { label: 'Phone number', value: renter.value?.user.phoneNumber || 'N/A' },
  { label: 'Email', value: renter.value?.user.email || 'N/A' },
]);

const renterHighlights = computed<{ label: string; value: string | number | undefined }[]>(() => [
  { label: 'Credit score', value: renter.value?.creditScore || 'N/A' },
  { label: 'Desired lease term', value: renter.value?.desiredLeaseTerm || 'N/A' },
  { label: 'Monthly income', value: renter.value?.monthlyIncome ? formatCurrency(renter.value?.monthlyIncome) : 'N/A' },
  {
    label: 'Desired lease date',
    value: renter.value?.desiredMoveInDate
      ? formatDate(new Date(renter.value?.desiredMoveInDate), 'MM/DD/YYYY')
      : 'N/A',
  },
  { label: 'Occupation', value: renter.value?.occupation || 'N/A' },
  {
    label: 'Pets',
    value: renter.value?.hasPets === undefined ? 'N/A' : renter.value.hasPets ? 'Yes' : 'No',
  },
  {
    label: 'Housing voucher',
    value: renter.value?.hasHousingVoucher === undefined ? 'N/A' : renter.value.hasHousingVoucher ? 'Yes' : 'No',
  },
]);

onMounted(() => {
  renterProfileStore.load(renterId.value);

  if (!propertyStore.isLoaded && !propertyStore.isLoading) {
    propertyStore.loadProperty(propertyId.value);
  }
});

onUnmounted(() => {
  renterProfileStore.$reset();
  propertyStore.$reset();
});
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.wrapper {
  @include screen-max-lg {
    flex-direction: column;
  }
}

.aside {
  max-width: 30rem;
  width: 100%;

  @include screen-max-lg {
    max-width: 100%;
  }
}
</style>
